import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { useHasRole } from '../../_shared/hooks/useCurrentUser';
import { useRestoreBaseline } from '../hooks/useRestoreBaseline';

interface AdminToolsSectionProps {
  addToast: (message: string, type: 'success' | 'error') => void;
}

/**
 * AdminToolsSection — 관리자 도구 섹션
 *
 * ROLE_ADMIN 인 경우에만 렌더됨.
 * [표준 상태로 복구] 버튼 → 확인 다이얼로그 → useRestoreBaseline mutation.
 */
export function AdminToolsSection({ addToast }: AdminToolsSectionProps) {
  const { t } = useTranslation();
  const isAdmin = useHasRole('ROLE_ADMIN');
  const [dialogOpen, setDialogOpen] = useState(false);

  const restoreMutation = useRestoreBaseline(addToast);

  const handleOpenDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    if (!restoreMutation.isPending) {
      setDialogOpen(false);
    }
  }, [restoreMutation.isPending]);

  const handleConfirmRestore = useCallback(() => {
    restoreMutation.mutate(undefined, {
      onSettled: () => {
        setDialogOpen(false);
      },
    });
  }, [restoreMutation]);

  // ROLE_ADMIN 이 아니면 섹션 전체 미렌더
  if (!isAdmin) return null;

  return (
    <>
      <section
        aria-labelledby="admin-tools-heading"
        style={{
          marginTop: 'var(--spacing-8)',
          padding: 'var(--spacing-6)',
          background: 'color-mix(in srgb, var(--color-danger) 6%, var(--color-surface-background))',
          border: '1px solid color-mix(in srgb, var(--color-danger) 30%, var(--color-surface-border))',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        {/* 섹션 제목 */}
        <h2
          id="admin-tools-heading"
          style={{
            margin: 0,
            marginBottom: 'var(--spacing-2)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-danger)',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('myProfile.adminTools.title')}
        </h2>

        {/* 설명 */}
        <p
          style={{
            margin: 0,
            marginBottom: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('myProfile.adminTools.restore.desc')}
        </p>

        {/* [표준 상태로 복구] 버튼 */}
        <button
          type="button"
          onClick={handleOpenDialog}
          disabled={restoreMutation.isPending}
          aria-describedby="admin-tools-restore-desc"
          style={{
            padding: 'var(--spacing-2) var(--spacing-5)',
            background: 'var(--color-danger)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            fontFamily: 'var(--font-family-sans)',
            cursor: restoreMutation.isPending ? 'not-allowed' : 'pointer',
            opacity: restoreMutation.isPending ? 0.6 : 1,
            transition: 'opacity 0.15s',
          }}
          data-testid="admin-restore-baseline-btn"
        >
          {restoreMutation.isPending
            ? '복구 중...'
            : t('myProfile.adminTools.restore.button')}
        </button>

        {/* hidden desc for aria-describedby */}
        <span id="admin-tools-restore-desc" style={{ display: 'none' }}>
          {t('myProfile.adminTools.restore.desc')}
        </span>
      </section>

      {/* 확인 다이얼로그 */}
      <FormDialog
        open={dialogOpen}
        title={t('myProfile.adminTools.restore.confirmTitle')}
        onClose={handleCloseDialog}
        onCancel={handleCloseDialog}
        onSubmit={handleConfirmRestore}
        cancelLabel={t('myProfile.adminTools.restore.confirmCancel')}
        submitLabel={
          restoreMutation.isPending
            ? '복구 중...'
            : t('myProfile.adminTools.restore.confirmOk')
        }
        submitTestId="admin-restore-baseline-confirm"
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-primary)',
            lineHeight: 'var(--line-height-relaxed)',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('myProfile.adminTools.restore.confirmBody')}
        </p>
      </FormDialog>
    </>
  );
}
